<script setup lang="ts">
    import { type PropType, ref, computed } from "vue";
    import Media from "@/components/Media.vue";
    import RothcoCta from "@/components/RothcoCta.vue";
    import type { Cta } from "@/helpers/builder-init/index";
    import { getCtaLink } from "@/helpers/interfaces/Cta";
    import {
        useRichTextFormatting,
        richTextHasText
    } from "@/helpers/composables/builderFieldUtility.ts";

    interface BannerMedia {
        desktop_media: string; 
        mobile_media: string; 
        alt_text: string; 
        bg_color: string;
        min_height: Number;
        max_height: Number;
        inset_banner: Boolean;
        aria_label: string; 
        ambient_video_desktop: boolean;
        default_video_muted_desktop: boolean,
        ambient_video_mobile: boolean;
        default_video_muted_mobile: boolean;
    }
    interface BannerContent {
        content_align_vertical: string; 
        content_align_horizontal: string; 
        text_bg_color: string; 
        text_alignment: string; 
        desktop_text_alignment: string; 
        mobile_text_below: string; 
    }
    interface Eyebrow {
        text: string; 
        color: string; 
    }
    interface Heading {
        text: string; 
        color: string; 
        font_size_mobile: Number; //?
        font_size_desktop: Number; //?
    }
    interface Description {
        text: string; 
        color: string; 
    }
    interface Badge {
        image: string; 
        width_desktop: Number;
        width_mobile: Number;
        alt_text: string; 
    }
    interface FeatureBar {
        feature_icons: FeatureBarIcon[];
        bg_color: string;
        text_color: string;
    }
    interface FeatureBarIcon {
        image: string;
        text: string;
        aria_label: string;
        alt_text: string;
    }

    const props = defineProps({
        banner_media: {
            type: Object as PropType<BannerMedia>,
            default() {
                return {};
            },
            required: true
        },
        banner_content: {
            type: Object as PropType<BannerContent>,
            default() {
                return {
                    content_align_vertical: "middle",
                    content_align_horizontal: "left",
                    text_alignment: "left"
                };
            },
            required: true
        },
        eyebrow: {
            type: Object as PropType<Eyebrow>,
            default() {
                return { text: "" };
            },
            required: true
        },
        heading: {
            type: Object as PropType<Heading>,
            default() {
                return {};
            },
            required: true
        },
        description: {
            type: Object as PropType<Description>,
            default() {
                return {};
            },
            required: true
        },
        badge: {
            type: Object as PropType<Badge>,
            default() {
                return {};
            },
            required: true
        },
        ctas: {
            type: Array as PropType<Cta[]>,
            default() {
                return {};
            },
            required: true
        },
        feature_bar: {
            type: Object as PropType<FeatureBar>,
            default() {
                return {};
            },
            required: true
        }
    });

    const cssVars = computed(() => {
        return {
            "--banner__icon-width-desktop": props.badge.width_desktop + "px",
            "--banner__icon-width-mobile": props.badge.width_mobile + "px",
        };
    });

    const bannerHeightStyling = computed(()=>{
        let styling = [];
        if(props.banner_media.min_height){
            styling.push(`min-height: ${props.banner_media.min_height}px`);
        }else if(!props.banner_media.mobile_media && !props.banner_media.desktop_media){
            styling.push(`min-height: 100px`);
        }
        if(props.banner_media.max_height) styling.push(`max-height: ${props.banner_media.max_height}px`);
        return styling;
    })

    const insetClass = computed(()=>{
        return props.banner_media.inset_banner ? 'page-inset' : '';
    })

    const contentAlignmentClass = computed(() => {
        return `banner--desktop-${props.banner_content.content_align_vertical}-${props.banner_content.content_align_horizontal}`;
    });

    const textAlignmentClasses = computed(() => {
        let arr = [`${props.banner_content.text_alignment}-align`];
        if (props.banner_content.desktop_text_alignment)
            arr.push(
                `desktop-${props.banner_content.desktop_text_alignment}-align`
            );
        return arr;
    });

    const mobileTextBelowClasses = computed(() => {
        return props.banner_content.mobile_text_below
            ? [
                  "banner--below-image",
                  `bg-mobile-only-${props.banner_content.text_bg_color ?? "rothcoBlack"}`
              ]
            : ["banner--bottom-image"];
    });

    const bannerBgColor = computed(()=>{
        return `bg-${props.banner_media.bg_color || 'rothcoWhite'}`
    })
    const bannerBgColorMobile = computed(()=>{
        return `bg-mobile-only-${props.banner_media.bg_color || 'rothcoWhite'}`
    })

    const textContentBgColor = computed(() => {
        return `bg-${props.banner_content.text_bg_color}`;
    });

    const textColorClass = computed(() => {
        return {
            eyebrow: `text-${props.eyebrow.color}`,
            heading: `text-${props.heading.color}`,
            description: `text-${props.description.color}`
        };
    });

    const getCtaClasses = (cta) => {
        let classes = [cta.style ? cta.style : "primary"];
        if (cta.size != "default") classes.push(cta.size);
        return classes;
    };

    const featureBarBgColor = computed(()=>{
        return `bg-${props.feature_bar.bg_color || 'rothcoBlack'}`
    })

    const featureBarTextColor = computed(()=>{
        return `text-${props.feature_bar.text_color || 'rothcoBlack'}`
    })

    const ambientVideoDesktop = computed(()=>{
        return props.banner_media.ambient_video_desktop;
    })

    const defaultAudioMutedDesktop = computed(()=>{
        return props.banner_media.default_video_muted_desktop;
    })

    const ambientVideoMobile = computed(()=>{
        return props.banner_media.mobile_media ? props.banner_media.ambient_video_mobile : ambientVideoDesktop.value;
    })

    const defaultAudioMutedMobile = computed(()=>{
        return props.banner_media.mobile_media ? props.banner_media.default_video_muted_mobile : defaultAudioMutedDesktop.value;
    })

    const visibleFeatureIcons = computed(()=>{
        const featureIcons = props?.feature_bar?.feature_icons?.map(feature=>({
            ...feature, text: feature?.text?.replace(/-/g, '\u2011') ?? ''
        })) ?? [];
        const arrLength = featureIcons.length ?? 0;
        if(arrLength == 0) return [];
        return arrLength == 1 ? featureIcons : featureIcons?.slice(0, (arrLength - (arrLength % 2)));
    })

    let isVideo = ref(false);
    let isMobileVideo = ref(false);
</script>
<template>
    <div>
        <div
            :class="[
                'banner',
                insetClass,
                contentAlignmentClass,
                mobileTextBelowClasses
            ]"
            :style="[cssVars, ...bannerHeightStyling]"
        >
            <div class="banner__background">
                <Media
                    :lazy="false"
                    v-if="banner_media.desktop_media"
                    class="banner__image banner__image--desktop"
                    :media="banner_media.desktop_media"
                    :alt="props.banner_media.alt_text"
                    :aria-label="props.banner_media.aria_label"
                    :autoplay="ambientVideoDesktop"
                    :muted="ambientVideoDesktop || defaultAudioMutedDesktop"
                    :loop="ambientVideoDesktop"
                    :playsinline="ambientVideoDesktop"
                    :controls="!ambientVideoDesktop"
                    :ambient="ambientVideoDesktop"
                    v-model:video="isVideo"
                    :style="props.banner_media.min_height ? `min-height: ${props.banner_media.min_height}px` : ''"
                />
                <Media
                    :lazy="false"
                    v-if="banner_media.mobile_media || banner_media.desktop_media"
                    class="banner__image banner__image--mobile"
                    :media="banner_media.mobile_media || banner_media.desktop_media"
                    :alt="props.banner_media.alt_text"
                    :aria-label="props.banner_media.aria_label"
                    :autoplay="ambientVideoMobile"
                    :muted="ambientVideoMobile || defaultAudioMutedMobile"
                    :loop="ambientVideoMobile"
                    :playsinline="ambientVideoMobile"
                    :controls="!ambientVideoMobile"
                    :ambient="ambientVideoMobile"
                    v-model:video="isMobileVideo"
                    :style="props.banner_media.min_height ? `min-height: ${props.banner_media.min_height}px` : ''"
                />
                <div v-if="!banner_media.mobile_media && !banner_media.desktop_media" :class="['no-image', bannerBgColor]" :style="bannerHeightStyling"></div>
            </div>
            <div :class="{
                'banner__content': true,
                [bannerBgColorMobile]: props.banner_content.mobile_text_below,
                'banner__no-content-desktop': isVideo && !ambientVideoDesktop,
                'banner__no-content-mobile': isMobileVideo && !ambientVideoMobile
            }">
                <div
                    :class="[
                        'banner__layout',
                        ...textAlignmentClasses,
                        textContentBgColor
                    ]"
                >
                    <div v-if="props.badge.image" class="banner__icon">
                        <img :src="props.badge.image" :alt="props.badge.alt_text" />
                    </div>
                    <div
                        v-if="props.eyebrow.text"
                        :class="[
                            'banner__eyebrow',
                            'eyebrow',
                            textColorClass.eyebrow
                        ]"
                    >
                        {{ props.eyebrow.text }}
                    </div>
                    <h2
                        v-if="richTextHasText(props.heading.text)"
                        v-html="useRichTextFormatting(props.heading.text)"
                        :class="[
                            'banner__headline',
                            'headline-xl-light',
                            textColorClass.heading
                        ]"
                    ></h2>
                    <p
                        v-if="richTextHasText(props.description.text)"
                        v-html="useRichTextFormatting(props.description.text)"
                        :class="[
                            'banner__description',
                            'paragraph-default',
                            textColorClass.description
                        ]"
                    ></p>
                    <div v-if="props.ctas.length > 0" class="banner__buttons">
                        <RothcoCta
                            v-for="(cta, index) in props.ctas"
                            :key="index"
                            :ctaType="cta.type"
                            :class="[...getCtaClasses(cta)]"
                            v-bind="getCtaLink(cta)"
                            :aria-label="cta.aria_label"
                        >
                            {{ cta.label }}
                        </RothcoCta>
                    </div>
                    <slot name="additional_content"></slot>
                </div>
            </div>
        </div>
        <div v-if="Array.isArray(visibleFeatureIcons) && visibleFeatureIcons.length > 0" :class="['feature-bar', featureBarBgColor]">
            <div v-for="(feature, i) in visibleFeatureIcons" :key="i" class="feature-content">
                <img class="feature-image" :src="feature.image" :aria-label="feature.aria_label" :alt="feature.alt_text" loading="lazy">
                <span :class="['feature-text headline-xs', featureBarTextColor]">{{ feature.text }}</span>
            </div>          
        </div>
    </div>
</template>
